import jstz from 'jstz';
import {memoize} from 'lodash';
import {DateTime, DateTimeFormatOptions, Duration, ToHumanDurationOptions} from 'luxon';

import {Localizer} from '../store/stateless/internationalizationSelectors';

export function formatDateTime(
  localize: Localizer,
  dateTime: DateTime,
  dateFormat: Intl.DateTimeFormatOptions,
  timeFormat: TimeFormatsEnum,
) {
  const timeFormatOptions = build12Or24hOptionsForFormat(timeFormat);
  const finalFormatOptions = {
    ...dateFormat,
    ...timeFormatOptions,
  };

  return localize(dateTime, finalFormatOptions);
}

/*
 * Date helpers.
 */

export function formatDate(localize: Localizer, date?: DateTime, dateFormat?: Intl.DateTimeFormatOptions) {
  return (date && localize(date, dateFormat || DateTime.DATE_SHORT)) || '';
}

/*
 * Time helpers.
 */

export enum TimeFormatsEnum {
  H12 = '12h',
  H24 = '24h',
}

export function formatTime(localize: Localizer, timeFormat: TimeFormatsEnum, time?: DateTime) {
  const format = build12Or24hFullOptionsForFormat(timeFormat);

  return (time && localize(time, format)) || '';
}

export function areTimesEqual(time1: DateTime, time2?: DateTime) {
  return Boolean(time2 && time1.hour === time2.hour && time1.minute === time2.minute);
}

/*
 * Timezone helpers.
 */

export const patchDateTimeFormat = memoize(
  (format: DateTimeFormatOptions): DateTimeFormatOptions => ({
    ...format,
    timeZone: findTimeZone(),
  }),
);

export const findTimeZone = () => {
  try {
    // Find the current timezone.
    const name = jstz.determine().name() || DateTime.local().zoneName;

    // Make sure it's valid in the current environment.
    Intl.DateTimeFormat(undefined, {timeZone: name});
    return name;
  } catch (error) {
    return 'UTC';
  }
};

const isHourCycleSupported =
  'hourCycle' in new Intl.DateTimeFormat('en-US', {hour: 'numeric'}).resolvedOptions();

// lib.dom.ts typings don't have `hourCycle` yet
// https://github.com/microsoft/TypeScript/issues/34399
const h24FormatOptions: Intl.DateTimeFormatOptions & {hourCycle?: string} = isHourCycleSupported
  ? {
      // As of Chromium 80, the default `hourCycle` is 'h24' so 'hour12: false` isn't actually the right way to do this.
      // This is according to the spec
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1045791#c7
      hourCycle: 'h23',
      hour12: undefined,
    }
  : {
      hour12: false,
    };

const h12FormatOptions: Intl.DateTimeFormatOptions = isHourCycleSupported
  ? {
      hourCycle: 'h12',
      hour12: undefined,
    }
  : {
      hour12: true,
    };

export function build12Or24hOptionsForFormat(timeFormat: TimeFormatsEnum) {
  return timeFormat === TimeFormatsEnum.H24 ? h24FormatOptions : h12FormatOptions;
}

export function build12Or24hFullOptionsForFormat(timeFormat: TimeFormatsEnum) {
  const shouldUse24h = timeFormat === TimeFormatsEnum.H24;
  const base = shouldUse24h ? DateTime.TIME_24_SIMPLE : DateTime.TIME_SIMPLE;
  const overrides = shouldUse24h ? h24FormatOptions : h12FormatOptions;

  return {
    ...base,
    ...overrides,
  };
}

/**
 * Handles converting a string into a valid timestamp. If it can be converted into a number, we will treat
 * that as the timestamp to return. If we convert to a number and it fails, we will try using the date object
 * to convert to a valid timestamp. If we fail to convert using the date, we will return the date value of
 * Wed Dec 31 1969, as that is the start of epoch timestamps.
 *
 * @param value Value to convert
 * @returns Timestamp
 */
export function getValidTimestampFromValue(value: string): number {
  // Try to convert it to a number
  const convertedValue = Number(value);
  if (!isNaN(convertedValue)) {
    return convertedValue;
  }

  // Try to convert it via the date object
  const dateTimeParsed = new Date(value).getTime();
  if (!isNaN(dateTimeParsed)) {
    return dateTimeParsed;
  }

  // Return the start of epoch
  return new Date(0).getTime();
}

export function formatDuration(value: Duration, options: ToHumanDurationOptions & {showZero?: boolean} = {}) {
  const {years, months, days, hours, minutes, seconds} = value.shiftTo(
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
  );

  const {showZero, ...toHumanOptions} = options;

  return Duration.fromObject({
    years: years || (showZero ? 0 : undefined),
    months: months || (showZero ? 0 : undefined),
    days: days || (showZero ? 0 : undefined),
    hours: hours || (showZero ? 0 : undefined),
    minutes: minutes || (showZero ? 0 : undefined),
    seconds: seconds || (showZero ? 0 : undefined),
  }).toHuman(toHumanOptions);
}
